import { getUnitMarkupFromCapabilityVariable, convertValueUnit, getUnitDefinitionFromCfJsonForVariable } from '@metservicedev/cf-units';
import { CfJson } from '@metservicedev/platforms-services-interface/src/model/CfJson';
import moment, { Moment } from 'moment-timezone';

import convertNetcdfjsonToLineByLineDatum from '../../../utils/convertNetcdfjsonToLineByLineDatum';
import dataPoint from '../../../utils/dataPoint';
import reduceDatasetsByViewblockState from '../../../utils/reduceDatasetsByViewblockState';
import valueExists from '../../../utils/valueExists';
import { MandateProps } from '../../types';
import { DatasetData, SingleCurrentValueState } from '../types';

type RequiredSingleCurrentValueState = MandateProps<SingleCurrentValueState, 'datasets' | 'viewblockState' | 'time'>;

const mergeTemplateAndData = (state: RequiredSingleCurrentValueState) => {
    const { datasets, viewblockState, time } = state;
    const { databilities, settings } = viewblockState;
    const { items = [], align, fontSize } = settings;

    const reportTime = moment(time.iso8601).tz(time.timezone);

    const massagedDatasets = reduceDatasetsByViewblockState({ datasets, viewblockState })
        .map((dataset) => {
            if (dataset?.isError) {
                return null;
            }

            dataset.data = convertNetcdfjsonToLineByLineDatum({ netcdfjson: dataset.cfjson as CfJson }) as DatasetData[];

            return dataset;
        })
        .filter((dataset) => dataset);

    return {
        ...state,
        model: {
            fontSize: fontSize || 100,
            align: align || 'center',
            items: items.map(({ id, databilityId, label, sf, unit }) => {
                const emptyItem = {
                    id,
                    label: '',
                    unit: '',
                    sf: sf > 0 ? sf : 3,
                };

                const wholeDatability = databilities.find(({ id }) => id == databilityId);

                if (!wholeDatability) {
                    return emptyItem;
                }

                const dataset = massagedDatasets.find((d) => d?.datasetId === wholeDatability.datasetId && d.capabilityId === wholeDatability.capabilityId);

                if (!dataset) return emptyItem;
                if (!dataset.cfjson) return emptyItem;

                const wholeCapability = dataset.capability!;

                const variable = Object.keys(wholeCapability.variables)[0];
                const sourceUnit = getUnitDefinitionFromCfJsonForVariable(dataset.cfjson, variable!)?.unit;
                const targetUnit = unit || sourceUnit || getUnitMarkupFromCapabilityVariable(wholeCapability);
                const data: (DatasetData & { time?: Moment })[] = dataset.data;

                const datums = data.filter((d) => d?.time);
                const orderedDatums = datums.sort(({ time: time1 }, { time: time2 }) => time2!.valueOf() - time1!.valueOf());
                const nearestDatum = orderedDatums.find((datum) => valueExists(datum, wholeCapability));

                const nearestValue = dataPoint(nearestDatum!, wholeCapability);
                const value = typeof nearestValue !== 'undefined' ? convertValueUnit({ from: sourceUnit!, to: targetUnit!, value: nearestValue! }) : '-';

                const time = nearestDatum ? nearestDatum.time!.tz(reportTime.tz()!) : null;

                return {
                    id,
                    label: label || wholeCapability?.display_name_short || '',
                    unit: targetUnit,
                    sf: sf > 0 ? sf : 3,
                    time,
                    value,
                };
            }),
        },
    };
};

export default mergeTemplateAndData;
export { RequiredSingleCurrentValueState };
